import React, { useEffect, useState } from 'react';
import './LoginPage.css';

type AzureLoginPageProps = {
  onLoginSuccess: (userName: string, userEmail: string) => void;
};

export const AzureLoginPage: React.FC<AzureLoginPageProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';
      const response = await fetch(`${API_BASE_URL}/auth/user`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const userInfo = await response.json();
          onLoginSuccess(userInfo.name, userInfo.email);
          return;
        }
        setError('认证接口返回了HTML，可能是反向代理未正确转发 /auth 到后端');
        setLoading(false);
        return;
      }
      setLoading(false);
    } catch (err: any) {
      setError(`检查认证状态失败: ${err?.message || String(err)}`);
      setLoading(false);
    }
  };

  const handleAzureLogin = () => {
    const AUTH_SERVER = 'https://cluster.tpcnailab.com';
    const APP_NAME = 'qc';
    const REDIRECT_URI = encodeURIComponent('https://aiqa.tpcnailab.com/auth/verify?next=/');

    window.location.href = `${AUTH_SERVER}/login/${APP_NAME}?redirect_uri=${REDIRECT_URI}`;
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-card" style={{ textAlign: 'center', alignItems: 'center' }}>
          <div className="login-logo-orbit">
            <div className="login-logo-core" />
          </div>
          <div style={{ marginTop: '12px', fontSize: '11px', color: '#9ca3af' }}>
            正在检查登录状态...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-hero">
        <div className="login-logo-orbit">
          <div className="login-logo-core" />
        </div>
        <div className="login-hero-text">
          <div className="login-hero-title">TPCN GenAI Lab. · 智能语音质检控制台</div>
          <div className="login-hero-sub">
            为质检团队打造的端到端语音分析中台，统一评估礼貌、合规、风险与体验分。
          </div>
          <div className="login-hero-bullets">
            <span>· 支持通话录音质检</span>
            <span>· 企业级 Azure AD 统一身份认证</span>
          </div>
        </div>
      </div>

      <div className="login-card">
        <div className="login-card-header">
          <div className="login-card-title">登录 TPCN GenAI Lab. 控制台</div>
          <div className="login-card-sub">
            使用企业 Microsoft 账户安全登录
          </div>
        </div>

        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        <div style={{ marginTop: '8px' }}>
          <button
            className="azure-login-btn"
            onClick={handleAzureLogin}
          >
            <svg width="18" height="18" viewBox="0 0 23 23" fill="none">
              <path d="M0 0h10.931v10.931H0V0z" fill="#f25022"/>
              <path d="M12.069 0H23v10.931H12.069V0z" fill="#7fba00"/>
              <path d="M0 12.069h10.931V23H0V12.069z" fill="#00a4ef"/>
              <path d="M12.069 12.069H23V23H12.069V12.069z" fill="#ffb900"/>
            </svg>
            使用 Microsoft 账户登录
          </button>
        </div>

        <div className="login-info-box">
          <div className="login-info-icon">🔒</div>
          <div className="login-info-content">
            <div className="login-info-title">企业级安全认证</div>
            <div className="login-info-text">
              通过 Azure AD 统一身份认证，确保只有授权用户可以访问质检系统。
            </div>
          </div>
        </div>

        <div className="login-footnote">
          登录即表示您同意我们的服务条款和隐私政策
        </div>
      </div>
    </div>
  );
};
